/**
 * Capabilities: permissions + apis (both indexed on backend under browser.other.capabilities.*).
 */

import { withTiming } from '/utils.js';

const PERMISSION_QUERY_MS = 250;
const BARCODE_FORMATS_MS = 200;

/** Exported for fallback if the whole collect step fails. */
export const CAPABILITY_PERMISSION_NAMES = [
    'geolocation',
    'notifications',
    'microphone',
    'camera',
    'midi',
    'clipboard-read',
    'clipboard-write',
    'window-management',
];

function permissionQueryTimeout() {
    return new Promise((_, reject) => {
        setTimeout(() => reject(new Error('permission_query_timeout')), PERMISSION_QUERY_MS);
    });
}

function barcodeFormatsTimeout() {
    return new Promise((_, reject) => {
        setTimeout(() => reject(new Error('barcode_formats_timeout')), BARCODE_FORMATS_MS);
    });
}

/**
 * @param {() => boolean} fn
 * @returns {boolean}
 */
function safeBool(fn) {
    try {
        return !!fn();
    } catch {
        return false;
    }
}

/**
 * API surface flags for browser.other.capabilities.apis.* fingerprint paths.
 * @returns {Promise<Record<string, boolean|number|null>>}
 */
export async function collectApis() {
    /** @type {Record<string, boolean|number|null>} */
    const apis = {};

    apis.bluetooth = safeBool(() => 'bluetooth' in navigator);
    apis.barcodeDetector = safeBool(() => 'BarcodeDetector' in window);
    apis.webShare = safeBool(() => 'share' in navigator);
    apis.credentials = safeBool(() => 'credentials' in navigator);
    apis.paymentRequest = safeBool(() => 'PaymentRequest' in window);
    apis.usb = safeBool(() => 'usb' in navigator);
    apis.serial = safeBool(() => 'serial' in navigator);
    apis.nfc = safeBool(() => 'nfc' in navigator);
    apis.fileSystemAccess = safeBool(
        () => 'showOpenFilePicker' in window || 'showSaveFilePicker' in window
    );
    apis.eyeDropper = safeBool(() => 'EyeDropper' in window);
    apis.contactPicker = safeBool(
        () => 'contacts' in navigator && 'ContactsManager' in window
    );
    apis.permissionsApi = safeBool(
        () => !!navigator.permissions && typeof navigator.permissions.query === 'function'
    );

    apis.barcodeSupportedFormatsCount = null;
    if (apis.barcodeDetector && typeof window.BarcodeDetector?.getSupportedFormats === 'function') {
        try {
            const formats = await Promise.race([
                window.BarcodeDetector.getSupportedFormats(),
                barcodeFormatsTimeout(),
            ]);
            apis.barcodeSupportedFormatsCount = Array.isArray(formats) ? formats.length : null;
        } catch {
            apis.barcodeSupportedFormatsCount = null;
        }
    }

    return apis;
}

/**
 * @param {{ apis?: Record<string, unknown>, permissions?: Record<string, unknown> }} [details]
 * @returns {Promise<{ permissions: Record<string, string>, apis: Record<string, boolean|number|null> }>}
 */
export async function collectCapabilities(details) {
    const apisPromise = details
        ? withTiming('apis', details, () => collectApis())
        : collectApis();

    /** @type {Record<string, string>} */
    const permissions = {};

    const permissionsPromise = (async () => {
        if (!navigator.permissions || typeof navigator.permissions.query !== 'function') {
            for (let i = 0; i < CAPABILITY_PERMISSION_NAMES.length; i++) {
                permissions[CAPABILITY_PERMISSION_NAMES[i]] = 'unsupported';
            }
            return permissions;
        }

        const permissionTimings = details ? details.permissions : null;

        await Promise.all(
            CAPABILITY_PERMISSION_NAMES.map(async (name) => {
                const queryPermission = async () => {
                    try {
                        const status = await Promise.race([
                            navigator.permissions.query(
                                /** @type {PermissionDescriptor} */ ({ name })
                            ),
                            permissionQueryTimeout(),
                        ]);
                        const state = status && status.state;
                        permissions[name] =
                            state === 'granted' || state === 'denied' || state === 'prompt'
                                ? state
                                : 'error';
                    } catch {
                        permissions[name] = 'error';
                    }
                };

                if (permissionTimings) {
                    await withTiming(name, permissionTimings, queryPermission);
                } else {
                    await queryPermission();
                }
            })
        );

        return permissions;
    })();

    let apis;
    try {
        apis = await apisPromise;
    } catch (error) {
        await permissionsPromise.catch(() => {});
        throw error;
    }
    const resolvedPermissions = await permissionsPromise;

    return { permissions: resolvedPermissions, apis };
}
